import React, { useState } from 'react'
import { Box, Button, Card, Typography } from '@mui/material'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined'
import { useNavigate } from 'react-router-dom'
import { C, SX, TYPE } from '../../merchants/shared/tokens'
import { ROUTES, tatBaseName } from '../constants'
import { ReadinessChip } from '../../merchants/shared/StateChip'
import { agreementLines, agreementStatus, readiness } from '../components/MerchantStatus'
import BookingReadinessBanner from '../components/BookingReadiness'
import AgreementDialog from '../components/AgreementDialog'

/**
 * Body of the Done screen, after D2C Stage5_Done: what is now live for the merchant and where to go next. The primary
 * action opens the merchant in Merchant Update; the KPI cards above it (shell) show what was set up. The agreement box
 * opens the agreement (upload, dates, credit days, approval); it does not block booking.
 */
export default function PtlDoneScreen({ merchant, onChanged }) {
  const navigate = useNavigate()
  const ready = readiness(merchant).ready
  const [agreementOpen, setAgreementOpen] = useState(false)
  const ag = merchant.agreement
  const agStatus = agreementStatus(ag)
  return (
    <Box sx={{ maxWidth: 560, mx: 'auto', py: 4, px: 1, textAlign: 'center' }}>
      <Box aria-hidden sx={{ width: 64, height: 64, mx: 'auto', mb: 2, borderRadius: '50%', display: 'grid', placeItems: 'center', bgcolor: ready ? C.okSoft : C.warnSoft }}>
        {ready ? <CheckCircleOutlineIcon sx={{ fontSize: 36, color: C.ok }} /> : <WarningAmberOutlinedIcon sx={{ fontSize: 36, color: C.warn }} />}
      </Box>
      <Typography component='h1' sx={{ ...TYPE.pageTitle, fontSize: 22 }}>
        {ready ? `${merchant.name} is set up for PTL` : `${merchant.name}'s PTL set-up is done, but it cannot book yet`}
      </Typography>
      {!ready && <Box sx={{ mt: 2, textAlign: 'left' }}><BookingReadinessBanner merchant={merchant} onStep={false} onChanged={onChanged} /></Box>}
      <Card elevation={0} sx={{ ...SX.card, mt: ready ? 2 : 0, mb: 2, p: 2, textAlign: 'left', display: 'flex', flexDirection: 'column', gap: 0.75 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography component='h2' sx={{ ...TYPE.sectionTitle, flex: 1 }}>Agreement</Typography>
          <ReadinessChip tone={agStatus.tone} label={agStatus.label} />
        </Box>
        {agreementLines(ag).map((l) => <Typography key={l} sx={{ ...TYPE.rowHelp, color: C.inkBody }}>{l}</Typography>)}
        <Box sx={{ pt: 0.5 }}>
          <Button variant='outlined' onClick={() => setAgreementOpen(true)} sx={{ ...SX.ctaGhost, minHeight: 36 }}>
            {ag?.approvalStatus === 'NOT_SUBMITTED' ? 'Upload agreement' : 'Open agreement'}
          </Button>
        </Box>
      </Card>
      <AgreementDialog merchant={merchant} open={agreementOpen} onClose={() => setAgreementOpen(false)} onChanged={onChanged} />
      <Typography sx={{ ...TYPE.rowHelp, fontSize: 13.5, color: C.inkMuted, mt: 1, mb: 3 }}>
        New PTL orders are priced from its rate cards, get their TAT from {merchant.customTatLanes ? `its custom TAT and ${tatBaseName(merchant.tatCourier)}` : tatBaseName(merchant.tatCourier)},
        and are allocated to the couriers picked in Step 3.
      </Typography>
      <Box sx={{ display: 'grid', gap: 1.25 }}>
        <Button variant='contained' size='large' endIcon={<ArrowForwardIcon />} onClick={() => navigate(ROUTES.updateHub(merchant.id))}
          sx={{ ...SX.cta, fontSize: 14, py: 1.1 }}>
          Open in Merchant Update
        </Button>
        <Button variant='outlined' size='large' onClick={() => navigate(ROUTES.onboarding)} sx={{ ...SX.ctaGhost, fontSize: 14, py: 1.1 }}>
          Back to Onboarding Health
        </Button>
      </Box>
      <Typography sx={{ ...TYPE.rowHelp, mt: 2.5 }}>
        It uses the standard zone map. Give it its own map from Merchant Update only if its contract maps pincodes differently.
      </Typography>
    </Box>
  )
}
